import React, { useMemo } from 'react';
import { useTickets } from '../../../contexts/TicketContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Cell
} from 'recharts';
import {
    Map,
    Building2,
    Ticket,
    CheckCircle2,
    AlertTriangle,
    MessageSquare
} from 'lucide-react';

const CityTab = ({ dateFilter }) => {
    const { tickets } = useTickets();

    // --- Metrics Calculation ---
    const cityMetrics = useMemo(() => {
        const now = new Date();
        let startDate = new Date(0); // Default all time

        // Date Filtering Logic
        if (dateFilter === '24h') {
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        } else if (dateFilter === '7d') {
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else if (dateFilter === '30d') {
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        } else if (dateFilter === 'month') {
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        const filteredTickets = tickets.filter(t => new Date(t.createdAt) >= startDate);

        // Aggregation
        const cityMap = {};

        filteredTickets.forEach(t => {
            // Normalize City Name
            let city = t.city || 'Não Informado';
            if (city.toLowerCase().includes('capital')) city = 'Capital';
            else if (city.toLowerCase().includes('norte')) city = 'Valley Norte';
            else if (city.toLowerCase().includes('sul')) city = 'Valley Sul';

            if (!cityMap[city]) {
                cityMap[city] = {
                    name: city,
                    total: 0,
                    resolved: 0,
                    feedbackCount: 0,
                    totalRating: 0
                };
            }

            cityMap[city].total++;

            if (t.status === 'resolvido' || t.status === 'fechado') {
                cityMap[city].resolved++;
            }

            if (t.feedback && t.feedback.rating) {
                cityMap[city].feedbackCount++;
                cityMap[city].totalRating += Number(t.feedback.rating);
            }
        });

        const data = Object.values(cityMap).map(c => ({
            ...c,
            resolutionRate: c.total > 0 ? Math.round((c.resolved / c.total) * 100) : 0,
            avgRating: c.feedbackCount > 0 ? (c.totalRating / c.feedbackCount).toFixed(1) : 'N/A'
        })).sort((a, b) => b.total - a.total); // Sort by volume

        return data;
    }, [tickets, dateFilter]);

    const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cityMetrics.map((city, index) => (
                    <Card key={city.name} className="bg-black/40 border-white/10 backdrop-blur-md hover:bg-white/5 transition-all group">
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-start">
                                <CardTitle className="text-white text-lg font-bold flex items-center gap-2">
                                    <Map className={`h-5 w-5 ${index === 0 ? 'text-yellow-400' : 'text-gray-400'}`} />
                                    {city.name}
                                </CardTitle>
                                <div className={`px-2 py-1 rounded text-xs font-bold ${index === 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-800 text-gray-400'
                                    }`}>
                                    #{index + 1}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex justify-between items-end">
                                    <div>
                                        <p className="text-gray-400 text-xs uppercase font-bold">Volume</p>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-2xl font-bold text-white">{city.total}</span>
                                            <span className="text-xs text-gray-500">tickets</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-gray-400 text-xs uppercase font-bold">Resolvidos</p>
                                        <div className="flex items-center gap-1 justify-end text-green-400">
                                            <CheckCircle2 className="h-3 w-3" />
                                            <span className="font-bold">{city.resolutionRate}%</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-1 w-full bg-gray-800 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-indigo-500 transition-all duration-1000"
                                        style={{ width: `${city.resolutionRate}%` }}
                                    />
                                </div>

                                <div className="pt-2 border-t border-white/5 flex justify-between items-center text-xs">
                                    <div className="flex items-center gap-1 text-gray-400">
                                        <MessageSquare className="h-3 w-3" />
                                        <span>Avaliação:</span>
                                    </div>
                                    <span className={`font-bold ${city.avgRating >= 4 ? 'text-green-400' : city.avgRating >= 3 ? 'text-yellow-400' : 'text-gray-400'}`}>
                                        {city.avgRating === 'N/A' ? '-' : `⭐ ${city.avgRating}`}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Comparisons Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2 bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-indigo-400" />
                            Comparativo de Volume por Cidade
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="h-[350px]">
                        {cityMetrics.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={cityMetrics} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} />
                                    <XAxis type="number" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        stroke="#ffffff"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        width={100}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#ffffff05' }}
                                        contentStyle={{ backgroundColor: '#000000cc', borderColor: '#ffffff20', borderRadius: '8px', color: '#fff' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                    <Legend />
                                    <Bar dataKey="total" name="Total de Tickets" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                                        {cityMetrics.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-500">
                                <Map className="h-12 w-12 mb-4 opacity-20" />
                                <p>Nenhum dado encontrado para o período selecionado.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Detailed Stats List (Optional or simple list) */}
                <Card className="bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-yellow-400" />
                            Insights
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {cityMetrics.length > 0 ? (
                                <>
                                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                        <p className="text-xs text-blue-300 uppercase font-bold mb-1">Maior Demanda</p>
                                        <p className="text-white font-medium">
                                            A cidade <strong className="text-blue-400">{cityMetrics[0].name}</strong> lidera com {cityMetrics[0].total} tickets ({Math.round((cityMetrics[0].total / cityMetrics.reduce((a, b) => a + b.total, 0)) * 100)}% do total).
                                        </p>
                                    </div>

                                    {cityMetrics.some(c => c.avgRating !== 'N/A' && Number(c.avgRating) < 4) && (
                                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                            <p className="text-xs text-red-300 uppercase font-bold mb-1">Atenção Necessária</p>
                                            <p className="text-white font-medium">
                                                Algumas cidades apresentam média de avaliação abaixo de 4.0. Verifique os feedbacks em {cityMetrics.find(c => c.avgRating !== 'N/A' && Number(c.avgRating) < 4)?.name}.
                                            </p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm text-gray-500">Sem dados suficientes para insights.</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CityTab;
